import { useState, useEffect, useMemo, useRef } from 'react';
import { ProviderDeclaration } from '../../../questionnaire/Report/ProviderDeclaration/ProviderDeclaration';
import { ReportFooter } from '../../../questionnaire/Report/ReportFooter/ReportFooter';
import { ReportHeader } from '../../../questionnaire/Report/ReportHeader/ReportHeader';
import { Divider } from '../../../ui/Divider/Divider';
import { DynamicFieldGroup } from '../../../ui/DynamicFieldGroup/DynamicFieldGroup';
import { RichTextArea } from '../../../ui/EditableInput/RichTextArea';
import { H2 } from '../../../ui/H2/H2';
import { Input } from '../../../ui/Input/Input';
import {
  conformityStatement,
  harmonisedStandards,
  systemAndProviderInformation,
  notifiedBodyConformityAssessment,
  personalDataComplianceOptions
} from './DeclarationOfConformity.consts';
import Signature from '../../../ui/Signature/Signature';
import RadioButtonGroup from '../../../ui/RadioButton/RadioButtonGroup';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { setEUDeclarationField } from '../../../../store/slices/documentationSlice';
import { useEUDeclarationData } from '../../../../hooks/useEUDeclarationData';
import {
  EUDeclarationConformityStatement,
  EUDeclarationHarmonisedStandard,
  EUDeclarationPersonalDataCompliance
} from '../../../../types/EUDeclaration.types';

// Special keys for storing complex data as JSON strings in euDeclarationFields
export const CONFORMITY_STATEMENTS_KEY = '__conformityStatements__';
export const HARMONISED_STANDARDS_KEY = '__harmonisedStandards__';
export const PERSONAL_DATA_COMPLIANCE_KEY = '__personalDataCompliance__';
export const PROVIDER_DECLARATION_CHECKED_KEY =
  '__providerDeclarationChecked__';

export const DeclarationOfConformity = () => {
  const { headerData, footerData } = useEUDeclarationData();
  const dispatch = useAppDispatch();
  const { euDeclarationFields, createEUDeclarationResponse } = useAppSelector(
    (state) => state.documentation
  );
  const { providerDeclarationChecked } = useAppSelector(
    (state) => state.stepper
  );

  // Get personal data compliance from Redux or baseContent
  const personalDataCompliance =
    useMemo((): EUDeclarationPersonalDataCompliance | null => {
      const personalDataComplianceJson =
        euDeclarationFields[PERSONAL_DATA_COMPLIANCE_KEY]?.value;
      if (personalDataComplianceJson) {
        try {
          const parsed = JSON.parse(
            personalDataComplianceJson
          ) as EUDeclarationPersonalDataCompliance;
          return parsed;
        } catch (error) {
          console.error('Failed to parse personalDataCompliance:', error);
        }
      }
      // If no field in Redux, use baseContent from response
      return createEUDeclarationResponse?.personalDataCompliance ?? null;
    }, [euDeclarationFields, createEUDeclarationResponse]);

  // Determine selected value based on complied boolean or text
  const selectedPersonalDataComplianceValue = useMemo((): string => {
    if (personalDataCompliance) {
      // Priority 1: use complied boolean if it is explicitly set (true or false)
      if (personalDataCompliance.complied === true) {
        return personalDataComplianceOptions[0].value;
      }
      if (personalDataCompliance.complied === false) {
        return personalDataComplianceOptions[1].value;
      }
      // Priority 2: If complied is undefined, use text to determine selection
      if (
        personalDataCompliance.text === personalDataComplianceOptions[0].value
      ) {
        return personalDataComplianceOptions[0].value;
      }
      if (
        personalDataCompliance.text === personalDataComplianceOptions[1].value
      ) {
        return personalDataComplianceOptions[1].value;
      }
      // Priority 3: If text exists but doesn't match options, use it
      if (personalDataCompliance.text) {
        return personalDataCompliance.text;
      }
    }
    // Default: return empty string (no selection) - radio button will show no selection
    return '';
  }, [personalDataCompliance]);

  const [selectedSector, setSelectedSector] = useState('');

  // Convert conformityStatements from backend format to form format
  const conformityStatementsInitialValue = useMemo((): Record<
    string,
    string
  >[] => {
    const conformityStatementsJson =
      euDeclarationFields[CONFORMITY_STATEMENTS_KEY]?.value;
    if (conformityStatementsJson) {
      try {
        const statements = JSON.parse(
          conformityStatementsJson
        ) as EUDeclarationConformityStatement[];
        return statements.map((stmt) => ({
          'Union act / instrument': stmt.identifier || '',
          'Scope / subject': stmt.title || '',
          'Reference(s)': stmt.appliedAspectsClauses || ''
        }));
      } catch (error) {
        console.error('Failed to parse conformityStatements:', error);
      }
    } else if (
      createEUDeclarationResponse?.conformityStatements?.conformityStatements
    ) {
      return createEUDeclarationResponse.conformityStatements.conformityStatements.map(
        (stmt) => ({
          'Union act / instrument': stmt.identifier || '',
          'Scope / subject': stmt.title || '',
          'Reference(s)': stmt.appliedAspectsClauses || ''
        })
      );
    }
    return [];
  }, [euDeclarationFields, createEUDeclarationResponse]);

  // Convert harmonisedStandards from backend format to form format
  const harmonisedStandardsInitialValue = useMemo((): Record<
    string,
    string
  >[] => {
    const harmonisedStandardsJson =
      euDeclarationFields[HARMONISED_STANDARDS_KEY]?.value;
    if (harmonisedStandardsJson) {
      try {
        const standards = JSON.parse(
          harmonisedStandardsJson
        ) as EUDeclarationHarmonisedStandard[];
        return standards.map((std) => ({
          Identifier: std.identifier || '',
          Title: std.title || '',
          'Applied aspects / clauses': std.appliedAspectsClauses || ''
        }));
      } catch (error) {
        console.error('Failed to parse harmonisedStandards:', error);
      }
    } else if (
      createEUDeclarationResponse?.harmonisedStandards?.harmonisedStandards
    ) {
      return createEUDeclarationResponse.harmonisedStandards.harmonisedStandards.map(
        (std) => ({
          Identifier: std.identifier || '',
          Title: std.title || '',
          'Applied aspects / clauses': std.appliedAspectsClauses || ''
        })
      );
    }
    return [];
  }, [euDeclarationFields, createEUDeclarationResponse]);

  // Sync selectedSector with Redux state only on initial load, not after user interaction
  const hasUserInteracted = useRef(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Always update if value changed, unless user has interacted
    if (hasUserInteracted.current) {
      return;
    }

    if (selectedPersonalDataComplianceValue !== selectedSector) {
      setSelectedSector(selectedPersonalDataComplianceValue);
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [
    personalDataCompliance,
    selectedPersonalDataComplianceValue,
    selectedSector
  ]);

  const handleValueChange = (label: string, value: string, section: string) => {
    dispatch(setEUDeclarationField({ label, value, section }));
  };

  const handleConformityStatementsChange = (rows: Record<string, string>[]) => {
    const statements: EUDeclarationConformityStatement[] = rows
      .filter(
        (row) =>
          row['Union act / instrument'] ||
          row['Scope / subject'] ||
          row['Reference(s)']
      )
      .map((row) => ({
        identifier: row['Union act / instrument'] || '',
        title: row['Scope / subject'] || '',
        appliedAspectsClauses: row['Reference(s)'] || ''
      }));
    dispatch(
      setEUDeclarationField({
        label: CONFORMITY_STATEMENTS_KEY,
        value: JSON.stringify(statements),
        section: 'Conformity Statement'
      })
    );
  };

  const handleHarmonisedStandardsChange = (rows: Record<string, string>[]) => {
    const standards: EUDeclarationHarmonisedStandard[] = rows
      .filter(
        (row) =>
          row['Identifier'] || row['Title'] || row['Applied aspects / clauses']
      )
      .map((row) => ({
        identifier: row['Identifier'] || '',
        title: row['Title'] || '',
        appliedAspectsClauses: row['Applied aspects / clauses'] || ''
      }));
    dispatch(
      setEUDeclarationField({
        label: HARMONISED_STANDARDS_KEY,
        value: JSON.stringify(standards),
        section: 'Harmonised Standards / Common Specifications'
      })
    );
  };

  const handlePersonalDataComplianceChange = (value: string) => {
    hasUserInteracted.current = true;
    setSelectedSector(value);
    const isComplied = value === personalDataComplianceOptions[0].value;
    const compliance: EUDeclarationPersonalDataCompliance = {
      text: value || undefined,
      complied: isComplied
    };
    dispatch(
      setEUDeclarationField({
        label: PERSONAL_DATA_COMPLIANCE_KEY,
        value: JSON.stringify(compliance),
        section: 'Personal Data Compliance'
      })
    );
  };

  return (
    <div className="w-full h-full p-5">
      <ReportHeader componentInfo={headerData} />

      <Divider classes="my-[34px]" />

      {/* System and Provider Information Field Group */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2 title="System and Provider Information" textSize="text-base" />
        <Divider />
        {systemAndProviderInformation.fields.map((field) => {
          if (field.type === 'textArea') {
            return (
              <RichTextArea
                key={field.label}
                label={field.label}
                placeholder={field.placeholder}
                onChange={(value) =>
                  handleValueChange(
                    field.label,
                    value,
                    'System and Provider Information'
                  )
                }
                value={euDeclarationFields[field.label]?.value || ''}
              />
            );
          } else {
            return (
              <Input
                type={field.type === 'email' ? 'email' : 'text'}
                key={field.label}
                id={field.label}
                label={field.label}
                maxLength={255}
                onChange={(e) =>
                  handleValueChange(
                    field.label,
                    e.target.value,
                    'System and Provider Information'
                  )
                }
                value={euDeclarationFields[field.label]?.value || ''}
                placeholder={field.placeholder}
                labelClassName="label"
                inputClassName="input"
              />
            );
          }
        })}
      </div>

      <Divider classes="my-[34px]" />

      {/* Provider Declaration Check */}
      <ProviderDeclaration
        componentInfo={{
          title: 'Declaration',
          checkboxText:
            'We, the undersigned provider, hereby declare under our sole responsibility that the AI system identified above fulfils the requirements listed below.',
          checked: (() => {
            // Get from euDeclarationFields first, then from stepper state
            // Default to false if nothing is set
            const fieldValue =
              euDeclarationFields[PROVIDER_DECLARATION_CHECKED_KEY]?.value;
            if (fieldValue !== undefined) {
              return fieldValue === 'true';
            }
            // Default to false, not true
            return providerDeclarationChecked || false;
          })(),
          onCheckedChange: (checked: boolean) => {
            // Save checkbox state to euDeclarationFields
            dispatch(
              setEUDeclarationField({
                label: PROVIDER_DECLARATION_CHECKED_KEY,
                value: checked.toString(),
                section: 'Declaration'
              })
            );
          }
        }}
      />

      <Divider classes="my-[34px]" />

      {/* Conformity Statement Dynamic Field Group */}
      <DynamicFieldGroup
        title="Conformity Statement"
        description="This AI system is in conformity with the requirements set out in Section 2 of the AI Act (Articles 8-15).
                      Where applicable, this declaration also covers conformity with other relevant Union harmonisation legislation.
                      The applicable acts are listed here:"
        fields={conformityStatement}
        onChange={handleConformityStatementsChange}
        initialValue={conformityStatementsInitialValue}
      />

      <Divider classes="my-[34px]" />

      {/* Personal Data Compliance */}

      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2 title="Personal Data Compliance" textSize="text-base" />
        <Divider />
        <RadioButtonGroup
          name="Personal Data Compliance"
          selectedValue={selectedSector}
          onChange={handlePersonalDataComplianceChange}
          options={personalDataComplianceOptions}
        />
      </div>

      <Divider classes="my-[34px]" />

      {/* Harmonised Standards / Common Specifications Dynamic Field Group */}
      <DynamicFieldGroup
        title="Harmonised Standards / Common Specifications"
        description="This AI system is in conformity with the requirements set out in Section 2 of the AI Act (Articles 8–15).
                      Where applicable, this declaration also covers conformity with other relevant Union harmonisation legislation.
                      The applicable acts are listed here:"
        fields={harmonisedStandards}
        onChange={handleHarmonisedStandardsChange}
        initialValue={harmonisedStandardsInitialValue}
      />

      <Divider classes="my-[34px]" />

      {/* Notified Body & Conformity Assessment */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2
          title="Notified Body & Conformity Assessment"
          textSize="text-base"
        />
        <Divider />
        {notifiedBodyConformityAssessment.fields.map((field) => {
          if (field.type === 'textArea') {
            return (
              <RichTextArea
                key={field.label}
                label={field.label}
                placeholder={field.placeholder}
                onChange={(value) =>
                  handleValueChange(
                    field.label,
                    value,
                    'Notified Body & Conformity Assessment'
                  )
                }
                value={euDeclarationFields[field.label]?.value || ''}
              />
            );
          } else {
            return (
              <Input
                key={field.label}
                id={field.label}
                label={field.label}
                maxLength={255}
                onChange={(e) =>
                  handleValueChange(
                    field.label,
                    e.target.value,
                    'Notified Body & Conformity Assessment'
                  )
                }
                value={euDeclarationFields[field.label]?.value || ''}
                placeholder={field.placeholder}
                labelClassName="label"
                inputClassName="input"
              />
            );
          }
        })}
      </div>

      <Divider classes="my-[34px]" />

      {/* <Signature text="Signature" dont need signature for now />

      <Divider classes="my-3.5" /> */}

      <ReportFooter componentInfo={footerData} />
    </div>
  );
};
